import React, { FC } from "react";
import { Handle, NodeProps, Position } from "reactflow";
import { WebhookIcon } from "../Icons";
import { NodeDevModeHighlighter } from "./NodeDevModeHighlighter";
import { RicochetNodeData } from "./NodeData";

const selectedTags = (data: RicochetNodeData) => {
  const tags =
    data.tagNames?.filter((tag) => !!tag?.trim()) ||
    (data.tagName?.trim() ? [data.tagName.trim()] : []);
  return tags;
};

const actionLabel = (data: RicochetNodeData) => {
  if (data.action === "addTag") {
    const tags = selectedTags(data);
    if (tags.length > 1) return `Add ${tags.length} tags`;
    return tags.length === 1 ? `Add tag ${tags[0]}` : "Add tags";
  }

  if (data.action === "createTask") {
    return data.taskName ? `Create task ${data.taskName}` : "Create task";
  }

  if (data.action === "createNote") {
    return data.note ? "Add note" : "Add note";
  }

  if (data.action === "getTask") {
    return data.taskId ? `Get task ${data.taskId}` : "Get task";
  }

  if (data.action === "getTasks") {
    return "Get all tasks";
  }

  if (data.action === "getNotes") {
    return "Get all notes";
  }

  return data.statusName || data.statusId
    ? `Set status ${data.statusName || data.statusId}`
    : "Set status";
};

export const RicochetNode: FC<NodeProps<RicochetNodeData>> = ({
  id,
  isConnectable,
  selected,
  data,
}) => {
  const action = data.action || "updateStatus";
  const missingConfig =
    (action === "updateStatus" && !String(data.statusId ?? "").trim()) ||
    (action === "addTag" && selectedTags(data).length === 0) ||
    (action === "createTask" && !String(data.taskName ?? "").trim()) ||
    (action === "createNote" && !String(data.note ?? "").trim()) ||
    (action === "getTask" && !String(data.taskId ?? "").trim());

  return (
    <div
      className={`relative w-[260px] h-[88px] rounded bg-themed-card font-inter ${
        data.disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${
        selected ? "border-2 border-[#0F766E]" : "border border-themed-border"
      }`}
    >
      <Handle
        position={Position.Top}
        type="target"
        isConnectable={isConnectable}
        className="!min-h-[1px] !h-[1px] !top-[1px] !opacity-0 !border-0 pointer-events-none cursor-default"
      />
      <NodeDevModeHighlighter id={id} />
      <div className="p-[16px] flex flex-col gap-[4px]">
        <div className="flex items-center gap-[6px]">
          <div className="text-[#0F766E]">
            <WebhookIcon />
          </div>
          <div className="font-semibold text-base whitespace-nowrap overflow-hidden text-ellipsis">
            Ricochet
          </div>
        </div>
        <div
          className={`font-normal text-[13px] leading-[20px] whitespace-nowrap overflow-hidden text-ellipsis ${
            data.showErrors && missingConfig
              ? "text-[#F43F5E]"
              : "text-themed-textSecondary"
          }`}
        >
          {actionLabel(data)}
        </div>
      </div>
      <Handle
        position={Position.Bottom}
        type="source"
        isConnectable={isConnectable}
        className="!min-h-[1px] !h-[1px] !bottom-[1px] !opacity-0 !border-0 pointer-events-none cursor-default"
      />
    </div>
  );
};
